import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTable, useRowSelect } from 'react-table';
import './FoodSupplierList.css';
import HeaderMenu from '../Header/HeaderMenu';
import CreateFoodSupplier from '../FoodSupplier/CreateFoodSupplier';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Checkbox, FormControlLabel } from '@mui/material';

const data = [
  {
    name: 'ABC Food Supplier',
    country: 'Thailand',
    state: 'Bangkok',
    city: 'Bangkok',
    latitude: 13.7563,
    longitude: 100.5018,
    priceForFiveWeeks: '$250/night',
    review: '4/5',
  },
  {
    name: 'XYZ Food Supplier',
    country: 'Thailand',
    state: 'Bangkok',
    city: 'Bangkok',
    latitude: 13.7563,
    longitude: 100.5018,
    priceForFiveWeeks: '$300/night',
    review: '4.5/5',
  },
  {
    name: 'Delicious Meals',
    country: 'Thailand',
    state: 'Bangkok',
    city: 'Bangkok',
    latitude: 13.7563,
    longitude: 100.5018,
    priceForFiveWeeks: '$200/night',
    review: '3.5/5',
  },
];

// Helper function to calculate distance between two coordinates (Haversine formula)
const getDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of the Earth in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in km
};

const FoodSupplierList = () => {
  const [open, setOpen] = useState(false); // Control dialog open state
  const [selectedFoodSupplier, setSelectedFoodSupplier] = useState(null); // Track selected food supplier
  
  const [nameFilter, setNameFilter] = useState(''); // State to handle name search input
  const [cityFilter, setCityFilter] = useState(''); // State to handle city search input
  const [countryFilter, setCountryFilter] = useState(''); // State to handle country search input
  const [useNearMe, setUseNearMe] = useState(false); // Toggle for "near to me" filter
  const [userLocation, setUserLocation] = useState(null); // Store user's geolocation

  // Get user's location on component mount
  useEffect(() => {
    if (useNearMe && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.error('Error getting user location:', error);
        }
      );
    }
  }, [useNearMe]);

  const handleViewDetails = useCallback((foodSupplier) => {
    setSelectedFoodSupplier(foodSupplier); // Set selected food supplier correctly
    setOpen(true); // Open the dialog
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false); // Close dialog
  }, []);

  const handleDelete = useCallback((foodSupplierName) => {
    const confirmed = window.confirm(`Are you sure you want to delete ${foodSupplierName}?`);
    if (confirmed) {
      console.log(`Deleted ${foodSupplierName}`);
    }
  }, []);

  const handleApprove = useCallback((foodSupplierName) => {
    console.log(`Approved ${foodSupplierName}`);
  }, []);

  // Filter data based on separate filters for name, city, country, and location
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchesName = item.name.toLowerCase().includes(nameFilter.toLowerCase());
      const matchesCity = item.city.toLowerCase().includes(cityFilter.toLowerCase());
      const matchesCountry = item.country.toLowerCase().includes(countryFilter.toLowerCase());

      const isNearMe = userLocation && useNearMe
        ? getDistance(userLocation.latitude, userLocation.longitude, item.latitude, item.longitude) <= 50 // 50 km radius
        : true; // If "near to me" is not checked, show all

      return matchesName && matchesCity && matchesCountry && isNearMe;
    });
  }, [nameFilter, cityFilter, countryFilter, userLocation, useNearMe]);

  const columns = useMemo(
    () => [
      { Header: 'Food Supplier Name', accessor: 'name' },
      { Header: 'Country', accessor: 'country' },
      { Header: 'State', accessor: 'state' },
      { Header: 'City', accessor: 'city' },
      { Header: 'Review', accessor: 'review' },
      {
        Header: 'Actions',
        Cell: ({ row }) => (
          <div className="actions-cell">
            <Button
              size="small"
              color="primary"
              onClick={() => handleViewDetails(row.original)} // Open dialog
            >
              View Details
            </Button>
            <Button
              size="small"
              color="success"
              onClick={() => handleApprove(row.original.name)}
            >
              Approve
            </Button>
            <Button
              size="small"
              color="secondary"
              onClick={() => handleDelete(row.original.name)}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [handleViewDetails, handleDelete, handleApprove]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: filteredData, // Use the filtered data here
    },
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        {
          id: 'selection',
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <input type="checkbox" {...getToggleAllRowsSelectedProps()} />
          ),
          Cell: ({ row }) => (
            <input type="checkbox" {...row.getToggleRowSelectedProps()} />
          ),
        },
        ...columns,
      ]);
    }
  );

  return (
    <>
      {!open && <HeaderMenu />}
      <div className="food-supplier-list">
        <h2>Food Supplier List</h2>

        {/* Separate search inputs for Name, City, and Country */}
        <TextField
          label="Search by Name"
          variant="outlined"
          fullWidth
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          style={{ marginBottom: '20px' }}
        />
        <TextField
          label="Search by City"
          variant="outlined"
          fullWidth
          value={cityFilter}
          onChange={(e) => setCityFilter(e.target.value)}
          style={{ marginBottom: '20px' }}
        />
        <TextField
          label="Search by Country"
          variant="outlined"
          fullWidth
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value)}
          style={{ marginBottom: '20px' }}
        />

        {/* Near Me Checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={useNearMe}
              onChange={() => setUseNearMe((prev) => !prev)}
              name="useNearMe"
              color="primary"
            />
          }
          label="Show suppliers near me"
        />

         <table {...getTableProps()} className="table">
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th key={column.id} {...column.getHeaderProps()}>{column.render('Header')}</th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);
                return (
                  <tr key={row.id} {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <td
                        key={cell.column.id}
                        {...cell.getCellProps()}
                        data-label={cell.column.Header}  // Ensures labels for mobile view
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>

        {/* Dialog for showing CreateFoodSupplier form */}
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
          <DialogTitle>Food Supplier Details</DialogTitle>
          <DialogContent>
            {selectedFoodSupplier && (
              <CreateFoodSupplier foodSupplier={selectedFoodSupplier} isDialog={true} />
            )}
          </DialogContent>
		  
		  
		  
		  
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

export default FoodSupplierList;
